/**
 * Per-system rules for the summary drawing, extracted from `SystemSummary.tsx` so they can be
 * unit-tested without rendering the component. Each is a pure function of the system code and the
 * lines the calculator already produced.
 */

export const EW06_SYSTEM_CODE = 'SYS_EW06'
export const POLYLINE_PP_SYSTEM_CODE = 'SYS_POLYLINE_PP'
/** KombiLine Mantelstein draws its own layout, see `KmmiDrawing`. */
export const KMMI_SYSTEM_CODE = 'SYS_KOMBILINE_MANTELSTEIN'
/**
 * KombiLine LBS has one inner pipe and plain keys, no `:zug{n}`, so it uses the generic stack. Its
 * inner pipe writes the keys KMMI and EW 06 use for the same parts, see
 * `kombiline-lbs-system-design.ts`.
 */
export const KOMBILINE_LBS_SYSTEM_CODE = 'SYS_KOMBILINE_LBS'

/** Bogen mit Stützfuß. The backend picks that product by the same suffix, see `ew06-skus.ts`. */
const EW06_BASE_BEND_SKU_SUFFIX = '167'

/**
 * The drawing names its slots after the DW key set. Systems listed here additionally render the
 * categories those slots miss; for anything else a calculated and billed line stays undrawn.
 * KombiLine LBS draws its inner pipe this way and adds its shaft rows and the Gleitmittel in
 * `SystemSummary.tsx`.
 */
const SYSTEMS_WITH_COMPLETE_DRAWING = [EW06_SYSTEM_CODE, POLYLINE_PP_SYSTEM_CODE, KOMBILINE_LBS_SYSTEM_CODE]

export function drawsEveryCategory(systemCode: string | undefined) {
  return systemCode !== undefined && SYSTEMS_WITH_COMPLETE_DRAWING.includes(systemCode)
}

/**
 * `kombiline-lbs-system-design.ts` keys each LBS shaft line `KLBS:Schacht:<classification>`. Lines
 * saved before STO-86 carry the plain key.
 */
export const LBS_SHAFT_KEY = 'KLBS:Schacht'

export function isLbsShaftKey(category: string) {
  return category === LBS_SHAFT_KEY || category.startsWith(`${LBS_SHAFT_KEY}:`)
}

const shaftKey = (classification: string) => `${LBS_SHAFT_KEY}:${classification}`

/**
 * The LBS shaft rows. The Abdeckplatte closes the shaft below the mouth, the elements and the
 * Putztür run beside the pipe, the plates or the wall console with its Distanzplatte carry it under
 * the footing (STO-75).
 */
export const LBS_TOP_CATEGORIES = [shaftKey('coverPlates')]
export const LBS_SHAFT_CATEGORIES = [shaftKey('shaftElements'), shaftKey('shaftElementsOpen'), shaftKey('cleaningDoors')]
export const LBS_BOTTOM_CATEGORIES = [
  shaftKey('intermediateSupportPlates'),
  shaftKey('basePlates'),
  shaftKey('wallConsoles'),
  shaftKey('wallConsolesCombustible'),
]
const LBS_PLACED_CATEGORIES = [...LBS_TOP_CATEGORIES, ...LBS_SHAFT_CATEGORIES, ...LBS_BOTTOM_CATEGORIES]
const LBS_LOOSE_PART_ORDER = [shaftKey('shaftConnectors'), shaftKey('adhesive'), shaftKey('mountingBrackets'), 'EquipmentService']

/**
 * The categories of the LBS row of loose parts in a fixed order, because the lines arrive unordered:
 * Verbinder, Kleber, Montagewinkel, the Gleitmittel (`EquipmentService`), then every other shaft key
 * no row names, such as the plain key of an older configuration. A billed shaft part is drawn somewhere.
 */
export function lbsLoosePartCategories(categories: string[]) {
  const rank = (category: string) => {
    const index = LBS_LOOSE_PART_ORDER.indexOf(category)
    return index === -1 ? LBS_LOOSE_PART_ORDER.length : index
  }
  return categories
    .filter(
      (category) =>
        category === 'EquipmentService' || (isLbsShaftKey(category) && !LBS_PLACED_CATEGORIES.includes(category)),
    )
    .sort((a, b) => rank(a) - rank(b))
}

/**
 * Whether the T-Stück is the lowest part of the chimney, which puts the cleaning port above it
 * instead of below. PolyLine PP always qualifies, it has no separate Fußteil and files its
 * Kamineinführung under the footing classification, see `PolylinePpSystemDesign.calculateFooting`.
 * EW06 qualifies at Ausführung 87°, where the slot holds the Bogen mit Stützfuß. KombiLine LBS
 * follows the EW06 rule: its EW06 Überdruck pipe stands on the Bogen mit Stützfuß, and its PP pipe
 * files the Kamineinführung under `FootingService`, so no T-Stück line exists there.
 */
export function tConnectorIsBase(systemCode: string | undefined, tConnectorSkus: string[]) {
  if (systemCode === POLYLINE_PP_SYSTEM_CODE) return true
  if (systemCode !== EW06_SYSTEM_CODE && systemCode !== KOMBILINE_LBS_SYSTEM_CODE) return false
  return tConnectorSkus.some((sku) => sku.endsWith(EW06_BASE_BEND_SKU_SUFFIX))
}
